"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";
import { InfoIcon } from "lucide-react";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface InfoTooltipProps {
  content: string | React.ReactNode;
}
const InfoTooltip = ({ content }: InfoTooltipProps) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <InfoIcon className="ml-1" size={16} />
      </TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

interface IconTooltipProps {
  children?: React.ReactNode;
  content?: string | React.ReactNode;
  forceShow?: boolean;
  className?: string;
}
const IconTooltip = ({
  children,
  content,
  forceShow,
  className,
}: IconTooltipProps) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip open={forceShow}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {!!content && (
        <TooltipContent className={className}>{content}</TooltipContent>
      )}
    </Tooltip>
  </TooltipProvider>
);

interface TooltipExportProps {
  children: React.ReactNode;
  content: string | React.ReactNode;
  className?: string;
}
const TooltipExport = ({
  children,
  content,
  className,
}: TooltipExportProps) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger className={className}>{children}</TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export {
  TooltipExport as Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  InfoTooltip,
  IconTooltip,
};
